import React, { useEffect, useState } from 'react';

import { ServiceActionBar } from './ServiceActionBar';
import { ServiceActionBarFragmentProps } from './ServiceActionBarFragment.props';
import { CheckInObject, CheckOutObject, FormFieldItem, ServiceNodeAllocationData, Technician } from '../../../../@types';
import { AllocationSubmitData } from '../../../../@types/allocation.type';
import { CustomisePartActionCard, PartActionType } from '../../../../@types/partAction.type';
import { TaskCard } from '../../../../@types/task.type';
import { Snackbar } from '../../../../components/atoms/Snackbar';
import { useAddBulkPartActions, useDeletePartActionById, useGetPartActionByServiceId } from '../../../../queries/partAction-query';
import { useGetTechnicianListByLocationNodeId } from '../../../../queries/service-checkin-checkout-query';
import { useUpdateServiceAllocation } from '../../../../queries/service-query';
import { useGetNextServicesStatuses, useSaveServiceStatus } from '../../../../queries/service-status-query';
import { useCreateAndUpdateTask, useDeleteTaskById, useGetTaskByServiceId } from '../../../../queries/task-query';
import { mapPartActions } from '../../../../utils/part-action';

export const ServiceActionBarFragment: React.FC<ServiceActionBarFragmentProps> = ({
  data
}) => {
  const [newServiceType, setNewServiceType] = useState('-1');
  const [newServiceTypeNote, setNewServiceTypeNote] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [partActionCardDataset, setPartActionCardDataset] = useState<CustomisePartActionCard[]>([]);
  const [taskCardDataset, setTaskCardDataset] = useState<TaskCard[]>([]);

  const nextServicesStatusesQuery = useGetNextServicesStatuses(data.serviceStatusCode, data.serviceTypeCode, data.id);
  const saveServiceStatusQuery = useSaveServiceStatus(data.id);
  const updateServiceAllocationQuery = useUpdateServiceAllocation(data.id);
  const createAndUpdateTaskQuery = useCreateAndUpdateTask(data.id);
  const getTechnicianListByLocationNodeId = useGetTechnicianListByLocationNodeId(data.mainLocationNodeId || 0);
  const getPartActionsByServiceId = useGetPartActionByServiceId(data.id);
  const getTasksByServiceIdQuery = useGetTaskByServiceId(data.id);
  const deletePartActionById = useDeletePartActionById(data.id);
  const deleteTaskById = useDeleteTaskById(data.id);
  const addBulkPartActions = useAddBulkPartActions(data.id);

  useEffect(() => {
    data.id && getTasksByServiceIdQuery.refetch();
  }, [data.id]);

  useEffect(() => {
    if (getTasksByServiceIdQuery.data) {
      const taskData = [ ...getTasksByServiceIdQuery.data, ...taskCardDataset];
      const uniqueTaskData = [...new Map(taskData.map((taskItem) => [taskItem.id, taskItem])).values()];

      setTaskCardDataset(uniqueTaskData);
    }
  }, [getTasksByServiceIdQuery.data]);

  useEffect(() => {
    getTechnicianListByLocationNodeId.data && setTechnicians(getTechnicianListByLocationNodeId.data);
  }, [getTechnicianListByLocationNodeId.data]);

  useEffect(() => {
    if (data.serviceStatusCode && data.serviceTypeCode) {
      nextServicesStatusesQuery.refetch();
    }
  }, [data]);

  const handleNewServiceStatusSubmit = async(byPassInteractionCheck?: boolean, checkinData?: CheckInObject, bulkCheckinData?: Array<CheckInObject>, checkoutData?: CheckOutObject, actionTriggers?: FormFieldItem[]) => {
    if (newServiceType !== '-1') {
      const newStatus = {
        nextStatus: newServiceType,
        note: newServiceTypeNote,
        byPassInteractionCheck,
        checkinData,
        bulkCheckinData,
        checkoutData,
        ...(actionTriggers && { actionTriggers })
      };

      await saveServiceStatusQuery.mutateAsync(newStatus);
      if (actionTriggers) {
        getPartActionsByServiceId.refetch();
        getTasksByServiceIdQuery.refetch();
      }
    }
  };

  useEffect(()=> {
    getPartActionsByServiceId.refetch();
  },[data]);

  useEffect(() => {
    if (getPartActionsByServiceId.data) { 
      const array: CustomisePartActionCard[] = [];       
      let definedIndex = 1;         
      getPartActionsByServiceId.data.forEach((data) => {
        if (data.actionTypeCode === PartActionType.REPLACE) {
          const inValue = {
            id: definedIndex,
            partTypeId: data.installedOrMaintainedPartData?.partTypeId,  
            actionId: data.id,
            name: data.installedOrMaintainedPartData?.partTypeName,
            sw: data.installedOrMaintainedPartData?.softwareVersion,
            hw: data.installedOrMaintainedPartData?.hardwareVersion,
            fw: data.installedOrMaintainedPartData?.firmwareVersion,
            ft: data.installedOrMaintainedPartData?.fleetTag,
            serial: data.installedOrMaintainedPartData?.partSerial1,
            partId: data.installedOrMaintainedPartData?.partId,
            ns_partId: data.installedOrMaintainedPartData?.ns_partId,
            qty: data.installedOrMaintainedPartData?.qty,
            category: data.installedOrMaintainedPartData?.partTypeCatagoryCode,
            condition: data.installedOrMaintainedPartData?.condition,
            location: data.installedOrMaintainedPartData?.location,
            notes: data.notes,
            type: data.actionTypeCode,
            images: [],
            isInSiblingHierarchy: data.installedOrMaintainedPartData?.isInSiblingHierarchy,
            isAccepted: data.installedOrMaintainedPartData?.isAccepted
          };

          const outValue = {
            id: definedIndex,
            partTypeId: data.removedPartData?.partTypeId,
            actionId: data.id,
            name: data.removedPartData?.partTypeName,
            sw: data.removedPartData?.softwareVersion,
            hw: data.removedPartData?.hardwareVersion,
            fw: data.removedPartData?.firmwareVersion,
            ft: data.removedPartData?.fleetTag,
            serial: data.removedPartData?.partSerial1,
            partId: data.removedPartData.partId,
            ns_partId: data.removedPartData.ns_partId,
            qty: data.removedPartData?.qty,
            category: data.removedPartData?.partTypeCatagoryCode,
            condition: data.removedPartData?.condition,
            location: data.removedPartData?.location,
            locationId: data.removedPartData?.locationId,
            notes: data.notes,
            type: data.actionTypeCode,
            images: [],
            isInSiteHierarchy: data.removedPartData?.isInSiteHierarchy,
            isUnderClientStructure: data.removedPartData?.isUnderClientStructure,
            isAccepted: data.removedPartData?.isAccepted
          };
          array.push({
            value: inValue,
            inValue: inValue,
            outValue: outValue
          });
        } else if ( data.actionTypeCode === PartActionType.CLEAN ) {
          const saveValue = {
            id: definedIndex,
            partTypeId: data.installedOrMaintainedPartData?.partTypeId,
            actionId: data.id,
            name: data.installedOrMaintainedPartData?.partTypeName,
            sw: data.installedOrMaintainedPartData?.softwareVersion,
            hw: data.installedOrMaintainedPartData?.hardwareVersion,
            fw: data.installedOrMaintainedPartData?.firmwareVersion,
            ft: data.installedOrMaintainedPartData?.fleetTag,
            serial: data.installedOrMaintainedPartData?.partSerial1,
            partId: data.installedOrMaintainedPartData?.partId,
            ns_partId: data.installedOrMaintainedPartData?.ns_partId,
            qty: data.installedOrMaintainedPartData?.qty,
            category: data.installedOrMaintainedPartData?.partTypeCatagoryCode,
            condition: data.installedOrMaintainedPartData?.condition,
            location: data.installedOrMaintainedPartData?.location,
            notes: data.notes,
            type: data.actionTypeCode || '',
            images: [],
            isAccepted: data.installedOrMaintainedPartData?.isAccepted
          };
          array.push({
            value: saveValue,
            inValue: saveValue,
            outValue: saveValue
          });
          
        } else if ( data.actionTypeCode === PartActionType.TEST ) {
          const saveValue = {
            id: definedIndex,
            partTypeId: data.installedOrMaintainedPartData?.partTypeId,
            actionId: data.id,
            name: data.installedOrMaintainedPartData?.partTypeName,
            sw: data.installedOrMaintainedPartData?.softwareVersion,
            hw: data.installedOrMaintainedPartData?.hardwareVersion,
            fw: data.installedOrMaintainedPartData?.firmwareVersion,
            ft: data.installedOrMaintainedPartData?.fleetTag,
            serial: data.installedOrMaintainedPartData?.partSerial1,
            partId: data.installedOrMaintainedPartData?.partId,
            ns_partId: data.installedOrMaintainedPartData?.ns_partId,
            qty: data.installedOrMaintainedPartData?.qty,
            category: data.installedOrMaintainedPartData?.partTypeCatagoryCode,
            condition: data.installedOrMaintainedPartData?.condition,
            location: data.installedOrMaintainedPartData?.location,
            notes: data.notes,
            type: data.actionTypeCode || '',
            images: [],
            isAccepted: data.installedOrMaintainedPartData?.isAccepted,
            testOutcome: data?.installedOrMaintainedPartData?.testOutcome,
            observedFaultId: data?.installedOrMaintainedPartData?.observedFaultId,
            observedFaultName: data?.installedOrMaintainedPartData?.observedFaultName,
            labourTime: data?.installedOrMaintainedPartData?.labourTime
          };
          array.push({
            value: saveValue,
            inValue: saveValue,
            outValue: saveValue
          });
          
        } else if (data.actionTypeCode === PartActionType.REMOVE) {
          const outValue = {
            id: definedIndex,
            partTypeId:data.removedPartData?.partTypeId,
            actionId: data.id,
            name: data.removedPartData?.partTypeName,
            sw: data.removedPartData?.softwareVersion,
            hw: data.removedPartData?.hardwareVersion,
            fw: data.removedPartData?.firmwareVersion,
            ft: data.removedPartData?.fleetTag,
            serial: data.removedPartData?.partSerial1,
            qty: data.removedPartData?.qty,
            partId: data.removedPartData?.partId,
            ns_partId: data.removedPartData?.ns_partId,
            condition: data.removedPartData?.condition,
            category: data.removedPartData?.partTypeCatagoryCode,
            location: data.removedPartData?.location,
            locationId: data.removedPartData?.locationId,
            notes: data.notes,
            type: data.actionTypeCode,
            images: [],
            isInSiteHierarchy: data.removedPartData?.isInSiteHierarchy,
            isUnderClientStructure: data.removedPartData?.isUnderClientStructure,
            isAccepted: data.removedPartData?.isAccepted
          };
          array.push({
            value: outValue,
            inValue: outValue,
            outValue: outValue
          });
        } else if (data.actionTypeCode === PartActionType.DOA) {
          const outValue = {
            id: definedIndex,
            partTypeId: data.removedPartData?.partTypeId,
            actionId: data.id,
            name: data.removedPartData?.partTypeName,
            sw: data.removedPartData?.softwareVersion,
            hw: data.removedPartData?.hardwareVersion,
            fw: data.removedPartData?.firmwareVersion,
            ft: data.removedPartData?.fleetTag,
            serial: data.removedPartData?.partSerial1,
            partId: data.removedPartData?.partId,
            ns_partId: data.removedPartData?.ns_partId,
            qty: data.removedPartData?.qty,
            category: data.removedPartData?.partTypeCatagoryCode,
            condition: data.removedPartData?.condition,
            location: data.removedPartData?.location,
            locationId: data.removedPartData?.locationId,
            notes: data.notes,
            type: data.actionTypeCode,
            images: [],
            isInSiteHierarchy: data.removedPartData?.isInSiteHierarchy,
            isUnderClientStructure: data.removedPartData?.isUnderClientStructure,
            isAccepted: data.removedPartData?.isAccepted
          };
          array.push({
            value: outValue,
            inValue: outValue,
            outValue: outValue
          });
        } else if (data.actionTypeCode === PartActionType.INSTALL) {
          const saveValue = {
            id: definedIndex,
            partTypeId: data.installedOrMaintainedPartData?.partTypeId,
            actionId: data.id,
            name: data.installedOrMaintainedPartData?.partTypeName,
            sw: data.installedOrMaintainedPartData?.softwareVersion,
            hw: data.installedOrMaintainedPartData?.hardwareVersion,
            fw: data.installedOrMaintainedPartData?.firmwareVersion,
            ft: data.installedOrMaintainedPartData?.fleetTag,
            serial: data.installedOrMaintainedPartData?.partSerial1,
            qty: data.installedOrMaintainedPartData?.qty,
            partId: data.installedOrMaintainedPartData?.partId,
            ns_partId: data.installedOrMaintainedPartData?.ns_partId,
            category: data.installedOrMaintainedPartData?.partTypeCatagoryCode,
            condition: data.installedOrMaintainedPartData?.condition,
            location: data.installedOrMaintainedPartData?.location,
            notes: data.notes,
            type: data.actionTypeCode,
            images: [],
            isInSiblingHierarchy: data.installedOrMaintainedPartData?.isInSiblingHierarchy,
            isAccepted: data.installedOrMaintainedPartData?.isAccepted,
            desiredParentSerial: data.installedOrMaintainedPartData?.desiredParentSerial,
            desiredParentId: data.installedOrMaintainedPartData?.desiredParentId,
            desiredParentLocationId: data.installedOrMaintainedPartData?.desiredParentLocationId,
            locationTypeCode: data.installedOrMaintainedPartData?.locationTypeCode
          };
          array.push({
            value: saveValue,
            inValue: saveValue,
            outValue: saveValue
          });
        } else if (data.actionTypeCode === PartActionType.REPAIR) {
          const saveValue = {
            id: definedIndex,
            partTypeId: data.installedOrMaintainedPartData?.partTypeId,
            actionId: data.id,
            name: data.installedOrMaintainedPartData?.partTypeName,
            sw: data.installedOrMaintainedPartData?.softwareVersion,
            hw: data.installedOrMaintainedPartData?.hardwareVersion,
            fw: data.installedOrMaintainedPartData?.firmwareVersion,
            ft: data.installedOrMaintainedPartData?.fleetTag,
            serial: data.installedOrMaintainedPartData?.partSerial1,
            qty: data.installedOrMaintainedPartData?.qty,
            partId: data.installedOrMaintainedPartData?.partId,
            ns_partId: data.installedOrMaintainedPartData?.ns_partId,
            category: data.installedOrMaintainedPartData?.partTypeCatagoryCode,
            condition: data.installedOrMaintainedPartData?.condition,
            location: data.installedOrMaintainedPartData?.location,
            notes: data.notes,
            repairActions: data?.installedOrMaintainedPartData?.repairActions?.split(','),
            labourTime: data?.installedOrMaintainedPartData?.labourTime,
            type: data.actionTypeCode,
            images: [],
            isAccepted: data.installedOrMaintainedPartData?.isAccepted
          };
          array.push({
            value: saveValue,
            inValue: saveValue,
            outValue: saveValue
          });
        } else if (data.actionTypeCode === PartActionType.ADJUST) {
          const saveValue = {
            id: definedIndex,
            partTypeId: data.installedOrMaintainedPartData?.partTypeId,
            actionId: data.id,
            name: data.installedOrMaintainedPartData?.partTypeName,
            sw: data.installedOrMaintainedPartData?.softwareVersion,
            hw: data.installedOrMaintainedPartData?.hardwareVersion,
            fw: data.installedOrMaintainedPartData?.firmwareVersion,
            ft: data.installedOrMaintainedPartData?.fleetTag,
            serial: data.installedOrMaintainedPartData?.partSerial1,
            qty: data.installedOrMaintainedPartData?.qty,
            partId: data.installedOrMaintainedPartData?.partId,
            ns_partId: data.installedOrMaintainedPartData?.ns_partId,
            category: data.installedOrMaintainedPartData?.partTypeCatagoryCode,
            condition: data.installedOrMaintainedPartData?.condition,
            location: data.installedOrMaintainedPartData?.location,
            notes: data.notes,
            type: data.actionTypeCode,
            images: [],
            isInSiblingHierarchy: data.installedOrMaintainedPartData?.isInSiblingHierarchy,
            isAccepted: data.installedOrMaintainedPartData?.isAccepted,
            desiredParentSerial: data.installedOrMaintainedPartData?.desiredParentSerial,
            desiredParentId: data.installedOrMaintainedPartData?.desiredParentId,
            desiredParentLocationId: data.installedOrMaintainedPartData?.desiredParentLocationId,
            locationTypeCode: data.installedOrMaintainedPartData?.locationTypeCode
          };
          array.push({
            value: saveValue,
            inValue: saveValue,
            outValue: saveValue
          });
        }
        definedIndex = definedIndex + 1;
      });

      const newDataSet = partActionCardDataset && partActionCardDataset.filter((obj) => !obj.value.actionId);
      setPartActionCardDataset([...array, ...newDataSet]);
    }
  }, [getPartActionsByServiceId.data, data.id]);

  const handleOnAllocationChange = (allocation: AllocationSubmitData) => {
    const allocationData: ServiceNodeAllocationData = {
      serviceId: data.id,
      userDepotAllocationNodeId: allocation.nodeId,
      allocationTypeCode: allocation.allocationTypeCode
    };
    updateServiceAllocationQuery.mutateAsync(allocationData);
  };

  const onChangePartActionCard = (updatedPartActionCardDataset: CustomisePartActionCard[], isTextChange?: boolean) => {
    setPartActionCardDataset(updatedPartActionCardDataset);
    !isTextChange && getPartActionsByServiceId.refetch();
  };

  const handleExecPartActions = async(partActionCardDataset: CustomisePartActionCard[], nonExecutablePartActions?: CustomisePartActionCard[]) => {
    let postExecutionPartActionsDataset = [];
    try {
      const mappedPartActions = mapPartActions(partActionCardDataset, data.id);
      const bulkSubmitResult: any = await addBulkPartActions.mutateAsync(mappedPartActions);
      if (!bulkSubmitResult?.interactionFormData?.length) {
        postExecutionPartActionsDataset = nonExecutablePartActions && nonExecutablePartActions?.length > 0 ? nonExecutablePartActions : [];
        setPartActionCardDataset(nonExecutablePartActions && nonExecutablePartActions?.length > 0 ? nonExecutablePartActions : []);
        setToastMessage('Executable part actions submit succeful');
        setOpenToast(true);
      } else {
        let updatedPartActionsDataset = partActionCardDataset?.map((card) => {
          if (!bulkSubmitResult?.interactionFormData?.some((interaction: any) => interaction?.partActionId === card.value.id )) {
            card.executionCompleted = true;
          }
          return card;
        });
        if (nonExecutablePartActions && nonExecutablePartActions?.length > 0 ) {
          updatedPartActionsDataset = updatedPartActionsDataset.concat(nonExecutablePartActions);
        }
        postExecutionPartActionsDataset = updatedPartActionsDataset;
        setPartActionCardDataset(updatedPartActionsDataset);
        if (updatedPartActionsDataset.some((card) => card.executionCompleted)) {
          setToastMessage('Executable part actions without interactions succefully submited');
          setOpenToast(true);
        }
      }
      return postExecutionPartActionsDataset;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setToastMessage('Part actions submit failed');
      setOpenToast(true);
    }
  };

  const onChangeTaskCard = (updatedTaskCardObject: TaskCard) => {
    const index = taskCardDataset.findIndex((taskCard) => taskCard.id === updatedTaskCardObject.id);
    if (index !== -1) {
      const updatedTaskCardDataset = [...taskCardDataset];

      updatedTaskCardDataset[index] = {
        ...updatedTaskCardDataset[index],
        taskData: updatedTaskCardObject.taskData,
      };

      setTaskCardDataset(updatedTaskCardDataset);
    }
  };

  const onSaveTaskCard = async (taskCardObject: TaskCard) => {
    await createAndUpdateTaskQuery.mutateAsync({
      taskTypeCode: taskCardObject.taskTypeCode,
      serviceId: taskCardObject.serviceId,
      taskData: taskCardObject.taskData,
      taskId: taskCardObject.id,
      execute: false
    });

    await getTasksByServiceIdQuery.refetch();
    
    if (taskCardObject.isNew) {
      const indexToRemove = taskCardDataset.findIndex((taskCard) => taskCard.id === taskCardObject.id);
      if (indexToRemove !== -1) {
        taskCardDataset.splice(indexToRemove, 1);
        setTaskCardDataset(taskCardDataset.filter((taskCard) => taskCard.isNew));
      }
    }

    setToastMessage('Succesfully Saved');
    setOpenToast(true);
  };

  const onExecTaskCard = async (taskCardObject: TaskCard) => {
    await createAndUpdateTaskQuery.mutateAsync({
      taskTypeCode: taskCardObject.taskTypeCode,
      taskTypeCategory: taskCardObject.taskTypeCategory,
      serviceId: taskCardObject.serviceId,
      taskData: taskCardObject.taskData,
      taskId: taskCardObject.id,
      execute: true
    });

    await getTasksByServiceIdQuery.refetch();


    const indexToRemove = taskCardDataset.findIndex((taskCard) => taskCard.id === taskCardObject.id);
    if (indexToRemove !== -1) {
      taskCardDataset.splice(indexToRemove, 1);
      setTaskCardDataset(taskCardObject.isNew ? [...taskCardDataset] : taskCardDataset.filter((taskCard) => taskCard.isNew));
    }

    setToastMessage('Succesfully Executed');
    setOpenToast(true);
  };

  const handleTaskClose = async (taskCardObject: TaskCard) => {
    if (!taskCardObject.isNew) {
      await deleteTaskById.mutateAsync({ id: taskCardObject.id });
      await getTasksByServiceIdQuery.refetch();
    }

    const indexToRemove = taskCardDataset.findIndex((taskCard) => taskCard.id === taskCardObject.id);

    if (indexToRemove !== -1) {
      taskCardDataset.splice(indexToRemove, 1);
      setTaskCardDataset([...taskCardDataset]);
    }
  };
  
  const handlePartActionCardClose = async (card: CustomisePartActionCard) => {
    const filteredPartActionCardDataset = partActionCardDataset.filter((partActionCardData) => partActionCardData.value.id !== card.value.id);
    setPartActionCardDataset(filteredPartActionCardDataset);

    if (card.value.actionId) {
      await deletePartActionById.mutateAsync({ id: card.value.actionId });
      getPartActionsByServiceId.refetch();
    }
  };

  return (
    <>
      <ServiceActionBar
        service={data}
        currentStatusLabel={data.serviceStatus?.name || ''}
        statusOptions={nextServicesStatusesQuery.data?.possibleNextStatuses || []}
        statusValue={newServiceType}
        suggestedOptions={nextServicesStatusesQuery.data?.preferedStatuses || []}
        primaryAllocation={data.primaryAllocation}
        secondaryAllocation={data.secondaryAllocation}
        noteText={newServiceTypeNote}
        technicians={technicians}
        isHelpdeskUser={data.isHelpdeskUser}
        onChangeStatusValue={(val) => setNewServiceType(val)}
        onNoteText={(val) => setNewServiceTypeNote(val)}
        onSubmit={handleNewServiceStatusSubmit}
        onAllocationChange={handleOnAllocationChange}
        partActionCardDataset={partActionCardDataset || []}
        onChangePartActionCard={onChangePartActionCard}
        onExecPartActions={handleExecPartActions}
        taskCardDataset={taskCardDataset}
        onChangeTaskCard={onChangeTaskCard}
        onSaveTaskCard={onSaveTaskCard}
        onExecTaskCard={onExecTaskCard}
        onCloseAndChangePopupTaskCard={handleTaskClose}   
        handlePartActionCardClose={handlePartActionCardClose}
      />
      <Snackbar
        open={openToast} 
        autoHideDuration={4000} 
        message={toastMessage ? toastMessage : 'Successfully Saved'}
        onClose={() => setOpenToast(false)}
      />
    </>
  );
};